import React, { useEffect, useState, useCallback } from "react";
import { BellRing, Github } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle } from "@/components/Kit";

const SIG = { Critical: "#A5300F", Notable: "#E39629", Informational: "#7F5F52" };
const STATUSES = ["New", "Acknowledged", "Investigating", "Resolved", "False Positive"];
const DISPOSITIONS = [
  { key: "ours", label: "Ours", c: "#2E7D32" },
  { key: "monitoring", label: "Monitoring", c: "#7F5F52" },
  { key: "takedown_filed", label: "Takedown Filed", c: "#E39629" },
  { key: "blocked", label: "Blocked", c: "#1F6FEB" },
  { key: "benign", label: "Benign", c: "#8A8175" },
];
const DISP = Object.fromEntries(DISPOSITIONS.map((d) => [d.key, d]));

export default function MonitoringInbox() {
  const { orgId } = useOrg();
  const [alerts, setAlerts] = useState([]);
  const [state, setState] = useState(null);
  const [fSig, setFSig] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    const params = { org_id: orgId };
    if (fSig) params.significance = fSig;
    if (fStatus) params.status = fStatus;
    http.get("/monitoring/alerts", { params }).then((r) => setAlerts(r.data));
    http.get("/monitoring/state", { params: { org_id: orgId } }).then((r) => setState(r.data)).catch(() => {});
  }, [orgId, fSig, fStatus]);
  useEffect(() => { setAlerts([]); setState(null); setOpen(null); }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [load, orgId]);

  const setStatus = async (a, status) => {
    try { await http.patch(`/monitoring/alerts/${a.id}`, { status }, { params: { org_id: orgId } }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setDisposition = async (a, disposition) => {
    const next = a.disposition === disposition ? "" : disposition;
    try { await http.patch(`/monitoring/alerts/${a.id}`, { disposition: next }, { params: { org_id: orgId } }); toast.success(next ? `Tagged “${DISP[next]?.label}”` : "Tag cleared"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F]">Alert Inbox</h1>
      <p className="text-sm text-[#8A8175] mt-1 mb-4">Continuous-monitoring change alerts across all watchers.</p>

      {state?.stale && (
        <Card className="mb-4 p-3 border-[#A5300F] bg-[#FCF1EE] text-sm text-[#A5300F]" data-testid="tick-stale-banner">
          ⚠ The monitoring scheduler has not ticked recently. Ensure the cron job calls <span className="font-mono">POST /api/internal/tick</span>.
        </Card>
      )}

      <div className="flex gap-3 mb-4">
        <select data-testid="filter-significance" value={fSig} onChange={(e) => setFSig(e.target.value)} className={sel}>
          <option value="">All significance</option>{["Critical", "Notable", "Informational"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel}>
          <option value="">All status</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <Card>
        <div className="divide-y divide-[#F4EFE8]" data-testid="alert-list">
          {alerts.map((a) => (
            <div key={a.id} className="p-4" data-testid={`alert-${a.id}`}>
              <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setOpen(open === a.id ? null : a.id)}>
                <div className="min-w-0">
                  <span className="text-[11px] font-bold uppercase tracking-wider mr-2" style={{ color: SIG[a.significance] }}>{a.significance}</span>
                  <span className="font-semibold text-sm">{a.subject_ref}</span>
                  <span className="text-xs text-[#8A8175] ml-2">{a.change_type} · {a.field}</span>
                  <div className="text-sm text-[#5C554C] mt-0.5">{a.rationale}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.disposition && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded text-white" style={{ backgroundColor: DISP[a.disposition]?.c || "#8A8175" }} data-testid={`disposition-badge-${a.id}`}>{DISP[a.disposition]?.label || a.disposition}</span>
                  )}
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-[#FFF5EB] text-[#8A8175]">{a.status}</span>
                  <span className="text-[11px] text-[#B5AC9E]">{new Date(a.detected_at).toLocaleString("en-GB")}</span>
                </div>
              </div>
              {open === a.id && (
                <div className="mt-3 pl-3 border-l-2 border-[#ECE6DD] text-xs space-y-1">
                  <div><span className="font-semibold text-[#8A8175]">Previous:</span> <span className="font-mono">{JSON.stringify(a.previous_value)}</span></div>
                  <div><span className="font-semibold text-[#8A8175]">Current:</span> <span className="font-mono">{JSON.stringify(a.current_value)}</span></div>
                  {a.current_value?.url && (
                    <div data-testid={`source-link-${a.id}`}>
                      <span className="font-semibold text-[#8A8175]">Source:</span>{" "}
                      <a href={a.current_value.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="text-[#A5300F] underline break-all inline-flex items-center gap-1">
                        <Github className="w-3 h-3" />{a.current_value.repo ? `${a.current_value.repo}${a.current_value.path ? "/" + a.current_value.path : ""}` : a.current_value.url}
                      </a>
                    </div>
                  )}
                  {a.current_value?.snippet && (
                    <div data-testid={`snippet-${a.id}`}>
                      <span className="font-semibold text-[#8A8175]">Match:</span>
                      <pre className="mt-1 text-[11px] bg-[#2B2622] text-[#E8DFD3] rounded p-2 overflow-x-auto whitespace-pre-wrap break-all font-mono">{a.current_value.snippet}</pre>
                    </div>
                  )}
                  <div><span className="font-semibold text-[#A5300F]">Action:</span> {a.recommendation}</div>
                  <div className="flex gap-1.5 pt-2">
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => setStatus(a, s)} data-testid={`set-${s.replace(/ /g, "-")}-${a.id}`}
                        className={`text-[11px] px-2 py-1 rounded border ${a.status === s ? "bg-[#A5300F] text-white border-[#A5300F]" : "border-[#E7D9C6] text-[#5C554C] hover:bg-[#FBF7F1]"}`}>{s}</button>
                    ))}
                  </div>
                  <div className="pt-2">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-[#8A8175] mb-1">Disposition / Takedown</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`disposition-controls-${a.id}`}>
                      {DISPOSITIONS.map((d) => (
                        <button key={d.key} onClick={() => setDisposition(a, d.key)} data-testid={`disposition-${d.key}-${a.id}`}
                          className="text-[11px] px-2 py-1 rounded border transition-colors"
                          style={a.disposition === d.key ? { backgroundColor: d.c, color: "#fff", borderColor: d.c } : { borderColor: "#E7D9C6", color: "#5C554C" }}>{d.label}</button>
                      ))}
                    </div>
                    {a.disposition_history?.length > 0 && (
                      <div className="mt-2 space-y-0.5" data-testid={`disposition-history-${a.id}`}>
                        {a.disposition_history.slice(-5).reverse().map((h, i) => (
                          <div key={`${h.at}-${h.disposition}-${i}`} className="text-[11px] text-[#8A8175]">
                            <span className="font-semibold" style={{ color: DISP[h.disposition]?.c || "#8A8175" }}>{h.disposition ? (DISP[h.disposition]?.label || h.disposition) : "Cleared"}</span>
                            {" · "}{h.by || "system"}{" · "}{new Date(h.at).toLocaleString("en-GB")}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {alerts.length === 0 && <div className="p-10 text-center text-[#B5AC9E]">No alerts. All monitored subjects are stable.</div>}
        </div>
      </Card>
    </div>
  );
}
const sel = "bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]";
